(() => {
class MyDialog extends HTMLElement {
  constructor() {
    super();
    this.activePageIndex = 0;
    this.dialog = this.querySelector("dialog");
    if (this.dialog) {
      dialogPolyfill.registerDialog(this.dialog);
    }
    this.pages = this.querySelectorAll(".page");
    this.closeButtons = this.querySelectorAll(".close-button");
    this.nextPageButtons = this.querySelectorAll(".next-page");
    this.radioButtons = this.querySelectorAll('input[type="radio"]');
    this.counselingResult = this.querySelectorAll(".counseling-result");
    this.nextPageDisableButtons = this.querySelectorAll(".next-page-disable");
    this.submitButtons = this.querySelectorAll('button[type="submit"]');
    this.q1_1 = this.querySelector(".q1_1");
    this.retryCounselingButton = this.querySelector(".retry-counseling");
  }

  connectedCallback() {
    if (!this.dialog) return;

    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog) {
        this.clearPendingForm();
        this.dialog.close();
      }
    });

    this.submitButtons.forEach((submitButton) => {
      submitButton.addEventListener("click", (event) => {
        event.preventDefault();
        // Close dialog first
        this.dialog.close();

        // Check if there's a pending form submission from quick-add button
        if (this.pendingForm && this.pendingSubmitButton) {
          // Submit the pending form
          if (this.pendingForm.requestSubmit) {
            this.pendingForm.requestSubmit();
          } else {
            // Fallback: create a submit event
            const submitEvent = new Event("submit", {
              bubbles: true,
              cancelable: true,
            });
            this.pendingForm.dispatchEvent(submitEvent);
          }
          // Clear pending references
          this.pendingForm = null;
          this.pendingSubmitButton = null;
        } else {
          // Find the product form element - scope to closest product-form
          const productForm = this.closest("product-form");
          if (productForm) {
            const form = productForm.querySelector(
              'form[data-type="add-to-cart-form"]'
            );
            if (form) {
              // Trigger form submission - product-form custom element will handle it
              if (form.requestSubmit) {
                form.requestSubmit();
              } else {
                // Fallback: dispatch submit event
                const submitEvent = new Event("submit", {
                  bubbles: true,
                  cancelable: true,
                });
                form.dispatchEvent(submitEvent);
              }
            }
          }
        }
      });
    });

    this.closeButtons.forEach((closeButton) => {
      const closeHandler = () => {
        this.clearPendingForm();
        this.dialog.close();
      };
      closeButton.addEventListener("click", closeHandler);
      closeButton.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          closeHandler();
        }
      });
    });

    this.nextPageButtons.forEach((nextPageButton) => {
      const nextPageHandler = () => {
        if (nextPageButton.style.display !== "none") {
          this.nextPage();
        }
      };
      nextPageButton.addEventListener("click", nextPageHandler);
      nextPageButton.addEventListener("keydown", (e) => {
        if (
          (e.key === "Enter" || e.key === " ") &&
          nextPageButton.style.display !== "none"
        ) {
          e.preventDefault();
          nextPageHandler();
        }
      });
    });

    if (this.pages.length > 0) {
      this.pages[this.activePageIndex].classList.add("active");
    }

    // Initialize Q1-1 visibility and button states
    this.updateQ1_1Visibility();
    this.checkNextButtonActive();

    Array.from(this.radioButtons).forEach((radioButton) => {
      radioButton.addEventListener("change", () => {
        this.updateQ1_1Visibility();
        this.checkNextButtonActive();
      });
    });

    if (this.retryCounselingButton) {
      const retryHandler = () => {
        this.retryCounseling();
      };
      this.retryCounselingButton.addEventListener("click", retryHandler);
      this.retryCounselingButton.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          retryHandler();
        }
      });
    }

  }

  updateQ1_1Visibility() {
    if (!this.q1_1) return;

    // Check if Q1 "はい" (value="1") is selected
    const q1Yes = this.querySelector('input[name="q1"][value="1"]');
    if (q1Yes && q1Yes.checked) {
      this.q1_1.classList.remove("tw-hidden");
    } else {
      this.q1_1.classList.add("tw-hidden");
      // Also uncheck Q2 radios when Q1-1 is hidden
      const q2Radios = this.querySelectorAll('input[name="q2"]');
      q2Radios.forEach((radio) => {
        radio.checked = false;
      });
    }
  }

  // Get the current state of all questions
  getQuestionStates() {
    return {
      q1: Array.from(this.querySelectorAll('input[name="q1"]')).some(
        (radio) => radio.checked
      ),
      q2: Array.from(this.querySelectorAll('input[name="q2"]')).some(
        (radio) => radio.checked
      ),
      q3: Array.from(this.querySelectorAll('input[name="q3"]')).some(
        (radio) => radio.checked
      ),
      isQ1Yes:
        this.querySelector('input[name="q1"][value="1"]')?.checked || false,
    };
  }

  // Toggle next page button visibility based on enabled state
  toggleNextButton(pageIndex, isEnabled) {
    const nextButton = this.nextPageButtons[pageIndex];
    const disableButton = this.nextPageDisableButtons[pageIndex];

    if (!nextButton || !disableButton) return;

    if (isEnabled) {
      disableButton.style.display = "none";
      nextButton.style.display = "flex";
      nextButton.classList.remove("tw-hidden");
    } else {
      nextButton.style.display = "none";
      nextButton.classList.add("tw-hidden");
      disableButton.style.display = "flex";
    }
  }

  checkNextButtonActive() {
    if (
      this.nextPageButtons.length === 0 ||
      this.nextPageDisableButtons.length === 0
    ) {
      return;
    }

    const states = this.getQuestionStates();

    // First page button logic
    // Q1=はいの場合: Q1とQ2の両方が必要
    // Q1=いいえの場合: Q1のみ必要
    const firstPageEnabled = states.isQ1Yes
      ? states.q1 && states.q2
      : states.q1;
    this.toggleNextButton(0, firstPageEnabled);

    // Second page button logic
    // Q1=はいの場合: Q1, Q2, Q3の全てが必要
    // Q1=いいえの場合: Q1とQ3が必要
    const secondPageEnabled = states.isQ1Yes
      ? states.q1 && states.q2 && states.q3
      : states.q1 && states.q3;
    this.toggleNextButton(1, secondPageEnabled);
  }

  retryCounseling() {
    if (this.pages.length > 0 && this.pages[this.activePageIndex]) {
      this.pages[this.activePageIndex].classList.remove("active");
    }
    this.activePageIndex = 0;
    if (this.pages.length > 0 && this.pages[this.activePageIndex]) {
      this.pages[this.activePageIndex].classList.add("active");
    }
    this.radioButtons.forEach((radioButton) => {
      radioButton.checked = false;
    });
    this.counselingResult.forEach((counselingResult) => {
      counselingResult.classList.remove("active");
    });
    this.updateQ1_1Visibility();
    this.checkNextButtonActive();
    this.dialog.scrollTop = 0;
  }

  // Clear pending form references to prevent submitting wrong product
  clearPendingForm() {
    this.pendingForm = null;
    this.pendingSubmitButton = null;
  }

  openDialog() {
    // Reset to first page
    this.pages.forEach((page, index) => {
      if (index === 0) {
        page.classList.add("active");
      } else {
        page.classList.remove("active");
      }
    });
    this.activePageIndex = 0;

    // Reset radio buttons
    this.radioButtons.forEach((radioButton) => {
      radioButton.checked = false;
    });

    // Reset counseling results
    this.counselingResult.forEach((counselingResult) => {
      counselingResult.classList.remove("active");
    });

    // Reset Q1-1 visibility and button states
    this.updateQ1_1Visibility();
    this.checkNextButtonActive();

    this.dialog.showModal();
    this.dialog.focus({ preventScroll: true });
    this.dialog.scrollTop = 0;
  }

  nextPage() {
    if (this.activePageIndex + 1 < this.pages.length) {
      this.pages[this.activePageIndex].classList.remove("active");
      this.activePageIndex++;
      this.pages[this.activePageIndex].classList.add("active");
    }
    if (this.activePageIndex === 2) {
      this.updateLastPage();
      window.BishodoCounseling.recordResult(this);
    }
    this.dialog.scrollTop = 0;
  }

  updateLastPage() {
    // Get radio values by name/value instead of index
    const q1No = this.querySelector('input[name="q1"][value="0"]');
    const q3No = this.querySelector('input[name="q3"][value="0"]');
    const q1Yes = this.querySelector('input[name="q1"][value="1"]');
    const q2No = this.querySelector('input[name="q2"][value="0"]');

    // Result 0: Q1=いいえ AND Q3=いいえ -> Can purchase
    if (q1No && q1No.checked && q3No && q3No.checked) {
      if (this.counselingResult[0])
        this.counselingResult[0].classList.add("active");
    }
    // Result 1: Q1=はい AND Q2=いいえ -> Cannot purchase
    else if (q1Yes && q1Yes.checked && q2No && q2No.checked) {
      if (this.counselingResult[1])
        this.counselingResult[1].classList.add("active");
    }
    // Result 2: All other cases -> Warning
    else {
      if (this.counselingResult[2])
        this.counselingResult[2].classList.add("active");
    }
  }

}

if (!customElements.get("my-dialog-shiseido")) {
  customElements.define("my-dialog-shiseido", MyDialog);
}


})();
